import {Component, computed, inject, input, signal, WritableSignal} from '@angular/core';
import {FormsModule} from '@angular/forms';

import {OptionsFacade, OptionsUpdateFacade} from '@selpia/options/data-access';
import {Layout, TileDisplay} from '@selpia/shared/models';
import {ButtonsSelectComponent, PopupToggleComponent, SvgSpriteIcon} from '@selpia/shared/ui';

interface LayoutOption {
  label: string;
  layout: Layout;
  icon: SvgSpriteIcon;
  disabled?: boolean;
}

interface TileOption {
  label: string,
  icon: SvgSpriteIcon,
  tileDisplay: TileDisplay,
  disabled?: boolean
}

@Component({
  selector: 'lib-feature-display-selector',
  imports: [ButtonsSelectComponent, PopupToggleComponent, FormsModule],
  templateUrl: './feature-display-selector.component.html',
  styleUrl: './feature-display-selector.component.css',
  host: {
    '(window:resize)': 'onWindowResize()',
    '[class.hideButton]': '!showTileOptions() && isNarrow()',
  },
})
export class FeatureDisplaySelectorComponent {
  readonly #BREAKPOINT = 1300;

  readonly optionsFacade = inject(OptionsFacade);
  readonly optionsUpdateFacade = inject(OptionsUpdateFacade);

  readonly showTileOptions = input(false);

  protected readonly tileOptions: TileOption[] = [
    {
      label: 'Tile with trend',
      icon: SvgSpriteIcon.BarChart,
      tileDisplay: TileDisplay.Trends,
    },
    {
      label: 'Tile with table',
      icon: SvgSpriteIcon.TableView,
      tileDisplay: TileDisplay.Table,
    },
  ];

  protected isListLayout = computed(() => this.selectedLayout().layout === Layout.List);

  protected readonly previewIcon = computed(() => {
    if (this.showTileOptions() && !this.isListLayout()) {
      return this.selectedTileOption().icon;
    }

    return this.selectedLayout().icon;
  });

  readonly selectedTileOption = computed(() => {
    const tileDisplay = this.optionsFacade.tileDisplay();
    return this.tileOptions.find(option => option.tileDisplay === tileDisplay) || this.tileOptions[0];
  });

  updateSelectedTileOption(option: TileOption): void {
    this.optionsUpdateFacade.setOptions({tileDisplay: option.tileDisplay});
  }

  protected readonly layoutOptions: LayoutOption[] = [
    {
      label: 'Grid view',
      layout: Layout.Grid,
      icon: SvgSpriteIcon.FunctionLine,
    },
    {
      label: 'List view',
      layout: Layout.List,
      icon: SvgSpriteIcon.ListCheck,
    },
  ];

  readonly selectedLayout = computed(() => {
    const layout = this.optionsFacade.layout();
    return this.layoutOptions.find(option => option.layout === layout) || this.layoutOptions[0];
  });

  updateSelectedLayout(layoutOption: LayoutOption): void {
    const layout = layoutOption.layout;
    this.optionsUpdateFacade.setOptions({layout});
  }

  forceGridLayout(): void {
    this.optionsUpdateFacade.setOptions({layout: Layout.Grid});
  }

  protected readonly isNarrow: WritableSignal<boolean> = signal(this.#displayCheck());

  #displayCheck() {
    return window.innerWidth < this.#BREAKPOINT;
  }

  #timeoutId: ReturnType<typeof setTimeout> | undefined;

  onWindowResize(): void {
    clearTimeout(this.#timeoutId);
    this.#timeoutId = setTimeout(() => {
      if (this.#displayCheck()) {
        this.forceGridLayout();
        this.isNarrow.set(true);
      } else {
        this.isNarrow.set(false);
      }
    }, 50);
  }

  protected readonly Layout = Layout;
}
